import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, Row, Col, Card, message, Radio, Spin, Table, Space, Modal, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { getProductsByClient } from '../../core/api/products';
import { 
  getAllVariableCosts, 
  createVariableCost, 
  updateVariableCost, 
  deleteVariableCost,
  getVariableCostsByOffer
} from '../../core/api/mediaplan';

const { Option } = Select;

const ProductCostForm = ({ clientId }) => {
  const [form] = Form.useForm();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [productCosts, setProductCosts] = useState([]);
  const [editingCost, setEditingCost] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const selectedClients = useSelector((state) => state.selectedClients);

  useEffect(() => {
    fetchProducts();
    fetchProductCosts();
  }, [selectedClients]);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProducts = async () => {
    setLoading(true);
    try {
      if (selectedClients && selectedClients.length > 0) {
        // Fetch products for the first selected client
        const response = await getProductsByClient(selectedClients[0]);
        setProducts(response.data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      message.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductCosts = async () => {
    setTableLoading(true);
    try {
      const response = await getAllVariableCosts();
      // Map the response data to include product_name for display in the table
      const costsWithNames = await Promise.all(response.data.map(async (cost) => {
        // Find the product name from the products array
        const product = products.find(p => p.id === cost.product);
        return {
          ...cost,
          product_name: product ? product.product_name : ` ${cost.product_name}`,
          product_code: product ? product.product_code : `Product ID: ${cost.product_name}`,
        };
      }));
      setProductCosts(costsWithNames);
    } catch (error) {
      console.error('Error fetching product costs:', error);
      message.error('Failed to fetch product costs');
    } finally {
      setTableLoading(false);
    }
  };
  
  // Function to fetch variable costs for a specific product
  const fetchCostsByProduct = async (productId) => {
    if (!productId) return;
    
    setTableLoading(true);
    try {
      const response = await getVariableCostsByOffer(productId);
      // If we have results, update the form with the first result
      if (response.data && response.data.length > 0) {
        const cost = response.data[0];
        form.setFieldsValue({
          merchant_fee: cost.merchant_fee,
          merchant_fee_value_type: cost.merchant_fee_value_type,
          call_center: cost.call_center,
          call_center_value_type: cost.call_center_value_type,
          shipping: cost.shipping,
          shipping_value_type: cost.shipping_value_type,
          fulfillment: cost.fulfillment,
          fulfillment_value_type: cost.fulfillment_value_type,
          royalty: cost.royalty,
          royalty_value_type: cost.royalty_value_type,
          refund: cost.refund,
          refund_value_type: cost.refund_value_type,
          product_cost: cost.product_cost,
          product_cost_value_type: cost.product_cost_value_type,
          mail_processing_cost: cost.mail_processing_cost,
          mail_processing_cost_value_type: cost.mail_processing_cost_value_type
        });
        message.info('Loaded existing cost data for this product');
      }
    } catch (error) {
      console.error('Error fetching costs by product:', error);
    } finally {
      setTableLoading(false);
    }
  };
  
  const handleEdit = (record) => {
    setEditingCost(record);
    editForm.setFieldsValue({
      product: record.product,
      merchant_fee: record.merchant_fee,
      merchant_fee_value_type: record.merchant_fee_value_type,
      call_center: record.call_center,
      call_center_value_type: record.call_center_value_type,
      shipping: record.shipping,
      shipping_value_type: record.shipping_value_type,
      fulfillment: record.fulfillment,
      fulfillment_value_type: record.fulfillment_value_type,
      royalty: record.royalty,
      royalty_value_type: record.royalty_value_type,
      refund: record.refund,
      refund_value_type: record.refund_value_type,
      mail_processing_cost: record.mail_processing_cost,
      mail_processing_cost_value_type: record.mail_processing_cost_value_type
    });
    setIsEditModalVisible(true);
  };
  
  const handleDelete = async (id) => {
    try {
      await deleteVariableCost(id);
      message.success('Product cost deleted successfully');
      fetchProductCosts();
    } catch (error) {
      console.error('Error deleting product cost:', error);
      message.error('Failed to delete product cost');
    }
  };
  
  const handleEditSubmit = async (values) => {
    setFormLoading(true);
    try {
      await updateVariableCost(editingCost.id, values);
      message.success('Product cost updated successfully');
      setIsEditModalVisible(false);
      fetchProductCosts();
    } catch (error) {
      console.error('Error updating product cost:', error);
      message.error('Failed to update product cost');
    } finally {
      setFormLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setFormLoading(true);
    try {
      // Check if this product already has costs configured
      if (isProductAlreadyConfigured(values.product)) {
        message.error('This product already has costs configured. Please edit the existing entry.');
        setFormLoading(false);
        return;
      }
      
      await createVariableCost(values);
      message.success('Product cost created successfully');
      form.resetFields();
      fetchProductCosts();
    } catch (error) {
      console.error('Error creating product cost:', error);
      message.error('Failed to create product cost');
    } finally {
      setFormLoading(false);
    }
  };

  const isProductAlreadyConfigured = (productId) => {
    return productCosts.some(cost => cost.product === productId);
  };

  const filterOption = (input, option) => {
    return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
  };

  // Define table columns for product costs
  const columns = [
    {
      title: 'Product',
      dataIndex: 'product_name',
      key: 'product_name',
      sorter: (a, b) => a.product_code?.localeCompare(b.product_code),
      render: (text) => text || 'N/A',
    },
    {
      title: 'Product Cost',
      dataIndex: 'product_cost',
      key: 'product_cost',
      render: (text, record) => `${parseFloat(text || 0).toFixed(2)} ${record.product_cost_value_type === 'percentage' ? '%' : '$'}`
    },
    {
      title: 'Merchant Fee',
      dataIndex: 'merchant_fee',
      key: 'merchant_fee',
      render: (text, record) => `${parseFloat(text || 0).toFixed(2)} ${record.merchant_fee_value_type === 'percentage' ? '%' : '$'}`
    },
    {
      title: 'Call Center',
      dataIndex: 'call_center',
      key: 'call_center',
      render: (text, record) => `${parseFloat(text || 0).toFixed(2)} ${record.call_center_value_type === 'percentage' ? '%' : '$'}`
    },
    {
      title: 'Shipping',
      dataIndex: 'shipping',
      key: 'shipping',
      render: (text, record) => `${parseFloat(text || 0).toFixed(2)} ${record.shipping_value_type === 'percentage' ? '%' : '$'}`
    },
    {
      title: 'Fulfillment',
      dataIndex: 'fulfillment',
      key: 'fulfillment',
      render: (text, record) => `${parseFloat(text || 0).toFixed(2)} ${record.fulfillment_value_type === 'percentage' ? '%' : '$'}`
    },
    {
      title: 'Royalty',
      dataIndex: 'royalty',
      key: 'royalty',
      render: (text, record) => `${parseFloat(text || 0).toFixed(2)} ${record.royalty_value_type === 'percentage' ? '%' : '$'}`
    },
    {
      title: 'Refund',
      dataIndex: 'refund',
      key: 'refund',
      render: (text, record) => `${parseFloat(text || 0).toFixed(2)} ${record.refund_value_type === 'percentage' ? '%' : '$'}`
    },
    {
      title: 'Mail Processing Cost',
      dataIndex: 'mail_processing_cost',
      key: 'mail_processing_cost',
      render: (text, record) => `${parseFloat(text || 0).toFixed(2)} ${record.mail_processing_cost_value_type === 'percentage' ? '%' : '$'}`
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            size="small"
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this product cost?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button 
              type="primary" 
              danger 
              icon={<DeleteOutlined />} 
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card 
      title="Product Cost" 
      className="form-card"
      style={{ marginTop: '20px', marginBottom: '20px' }}
      loading={loading}
    >
      <Spin spinning={formLoading}>
        <Form
          form={form}
          layout="vertical"
          size="small"
          onFinish={handleSubmit}
          initialValues={{
            merchant_fee_value_type: 'amount',
            call_center_value_type: 'amount',
            shipping_value_type: 'amount',
            fulfillment_value_type: 'amount',
            royalty_value_type: 'amount',
            refund_value_type: 'amount',
            mail_processing_cost_value_type: 'amount'
          }}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Product"
                name="product"
                rules={[{ required: true, message: 'Please select a product' }]}
              >
                <Select
                  placeholder="Select a product"
                  loading={loading}
                  showSearch
                  filterOption={filterOption}
                  onChange={(value) => fetchCostsByProduct(value)}
                >
                  {products.map(product => (
                    <Option key={product.id} value={product.id}>
                      {product.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Product Cost"
                name="product_cost"
                rules={[{ required: true, message: 'Please enter product cost' }]}
              >
                <Input type="number" step="0.0001" min="0" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Type"
                name="product_cost_value_type"
                initialValue="amount"
              >
                <Radio.Group>
                  <Radio value="amount">$</Radio>
                  <Radio value="percentage">%</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Call Center"
                name="call_center"
                rules={[{ required: true, message: 'Please enter call center cost' }]}
              >
                <Input type="number" step="0.0001" min="0" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Type"
                name="call_center_value_type"
                initialValue="amount"
              >
                <Radio.Group>
                  <Radio value="amount">$</Radio>
                  <Radio value="percentage">%</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Shipping"
                name="shipping"
                rules={[{ required: true, message: 'Please enter shipping cost' }]}
              >
                <Input type="number" step="0.0001" min="0" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Type"
                name="shipping_value_type"
                initialValue="amount"
              >
                <Radio.Group>
                  <Radio value="amount">$</Radio>
                  <Radio value="percentage">%</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Fulfillment"
                name="fulfillment"
                rules={[{ required: true, message: 'Please enter fulfillment cost' }]}
              >
                <Input type="number" step="0.0001" min="0" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Type"
                name="fulfillment_value_type"
                initialValue="amount"
              >
                <Radio.Group>
                  <Radio value="amount">$</Radio>
                  <Radio value="percentage">%</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Royalty"
                name="royalty"
                rules={[{ required: true, message: 'Please enter royalty cost' }]}
              >
                <Input type="number" step="0.0001" min="0" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Type"
                name="royalty_value_type"
                initialValue="amount"
              >
                <Radio.Group>
                  <Radio value="amount">$</Radio>
                  <Radio value="percentage">%</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Refund"
                name="refund"
                rules={[{ required: true, message: 'Please enter refund amount' }]}
              >
                <Input type="number" step="0.0001" min="0" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Type"
                name="refund_value_type"
                initialValue="amount"
              >
                <Radio.Group>
                  <Radio value="amount">$</Radio>
                  <Radio value="percentage">%</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Mail Processing Cost"
                name="mail_processing_cost"
                rules={[{ required: true, message: 'Please enter mail processing cost' }]}
              >
                <Input type="number" step="0.0001" min="0" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Type"
                name="mail_processing_cost_value_type"
                initialValue="amount"
              >
                <Radio.Group>
                  <Radio value="amount">$</Radio>
                  <Radio value="percentage">%</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Merchant Fee"
                name="merchant_fee"
                rules={[{ required: true, message: 'Please enter merchant fee' }]}
              >
                <Input type="number" step="0.0001" min="0" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Type"
                name="merchant_fee_value_type"
                initialValue="amount"
              >
                <Radio.Group>
                  <Radio value="amount">$</Radio>
                  <Radio value="percentage">%</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={formLoading}
            >
              Add Product Cost
            </Button>
          </Form.Item>
        </Form>
      </Spin>
      
      <div style={{ marginTop: '20px' }}>
        <Table
          columns={columns}
          dataSource={productCosts}
          rowKey="id"
          loading={tableLoading}
          pagination={{ pageSize: 10 }}
        />
      </div>
      
      <Modal
        title="Edit Product Cost"
        visible={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Spin spinning={formLoading}>
          <Form
            form={editForm}
            layout="vertical"
            size="small"
            onFinish={handleEditSubmit}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Product"
                  name="product"
                  rules={[{ required: true, message: 'Please select a product' }]}
                >
                  <Select
                    placeholder="Select a product"
                    loading={loading}
                    showSearch
                    filterOption={filterOption}
                    disabled
                  >
                    {products.map(product => (
                      <Option key={product.id} value={product.id}>
                        {product.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Product Cost"
                  name="product_cost"
                  rules={[{ required: true, message: 'Please enter product cost' }]}
                >
                  <Input type="number" step="0.0001" min="0" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Type"
                  name="product_cost_value_type"
                  initialValue="amount"
                >
                  <Radio.Group>
                    <Radio value="amount">$</Radio>
                    <Radio value="percentage">%</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Call Center"
                  name="call_center"
                  rules={[{ required: true, message: 'Please enter call center cost' }]}
                >
                  <Input type="number" step="0.0001" min="" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Type"
                  name="call_center_value_type"
                  initialValue="amount"
                >
                  <Radio.Group>
                    <Radio value="amount">$</Radio>
                    <Radio value="percentage">%</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Shipping"
                  name="shipping"
                  rules={[{ required: true, message: 'Please enter shipping cost' }]}
                >
                  <Input type="number" step="0.0001" min="0" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Type"
                  name="shipping_value_type"
                  initialValue="amount"
                >
                  <Radio.Group>
                    <Radio value="amount">$</Radio>
                    <Radio value="percentage">%</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Fulfillment"
                  name="fulfillment"
                  rules={[{ required: true, message: 'Please enter fulfillment cost' }]}
                >
                  <Input type="number" step="0.0001" min="0" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Type"
                  name="fulfillment_value_type"
                  initialValue="amount"
                >
                  <Radio.Group>
                    <Radio value="amount">$</Radio>
                    <Radio value="percentage">%</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Royalty"
                  name="royalty"
                  rules={[{ required: true, message: 'Please enter royalty cost' }]}
                >
                  <Input type="number" step="0.0001" min="0" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Type"
                  name="royalty_value_type"
                  initialValue="amount"
                >
                  <Radio.Group>
                    <Radio value="amount">$</Radio>
                    <Radio value="percentage">%</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Refund"
                  name="refund"
                  rules={[{ required: true, message: 'Please enter refund amount' }]}
                >
                  <Input type="number" step="0.0001" min="0" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Type"
                  name="refund_value_type"
                  initialValue="amount"
                >
                  <Radio.Group>
                    <Radio value="amount">$</Radio>
                    <Radio value="percentage">%</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Mail Processing Cost"
                  name="mail_processing_cost"
                  rules={[{ required: true, message: 'Please enter mail processing cost' }]}
                >
                  <Input type="number" step="0.0001" min="0" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Type"
                  name="mail_processing_cost_value_type"
                  initialValue="amount"
                >
                  <Radio.Group>
                    <Radio value="amount">$</Radio>
                    <Radio value="percentage">%</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={8}>
              <Form.Item
                label="Merchant Fee"
                name="merchant_fee"
                rules={[{ required: true, message: 'Please enter merchant fee' }]}
              >
                <Input type="number" step="0.0001" min="0" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Type"
                name="merchant_fee_value_type"
                initialValue="amount"
              >
                <Radio.Group>
                  <Radio value="amount">$</Radio>
                  <Radio value="percentage">%</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            </Row>

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={formLoading}
              >
                Update Product Cost
              </Button>
              <Button 
                style={{ marginLeft: 8 }} 
                onClick={() => setIsEditModalVisible(false)}
              >
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </Card>
  );
};

export default ProductCostForm;
